import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  RecordVoiceOver as VoiceIcon,
  Download as DownloadIcon,
  Article as ArticleIcon,
  PlaylistAddCheck as ClearQueueIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow } from 'date-fns';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { apiFetch } from '../services/httpClient';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Auth is httpOnly-cookie based (see AuthContext/api.js) — the browser sends
// the session cookie automatically on same-origin requests. Only attach an
// Authorization header if a legacy localStorage token actually exists;
// sending a synthesized "Bearer null" would make the server reject the
// request even though the valid cookie would otherwise authenticate it.
const getAuthToken = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return token && token !== 'null' && token !== 'undefined' ? token : null;
};

const getAuthHeaders = (includeJsonContentType = false) => {
  const token = getAuthToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

const FAILURE_TYPE_LABELS = {
  unsupported: 'Unsupported',
  parse_failed: 'Parse Failed',
  execution_failed: 'Execution Failed',
  recognition_failed: 'Recognition Failed',
  ai_unavailable: 'AI Unavailable',
};

const FAILURE_TYPE_COLORS = {
  unsupported: 'warning',
  parse_failed: 'info',
  execution_failed: 'error',
  recognition_failed: 'default',
  ai_unavailable: 'secondary',
};

const REVIEW_STATUS_LABELS = {
  new: 'New',
  reviewing: 'Reviewing',
  planned: 'Planned',
  shipped: 'Shipped',
  wont_fix: "Won't Fix",
};

// Where "clear the queue after exporting" moves records to. Planned keeps the
// record, its phrasing, and its frequency history intact while taking it out of
// the unresolved backlog the summary card ranks.
const RESET_TARGET_STATUS = 'planned';

const REVIEW_STATUS_COLORS = {
  new: 'warning',
  reviewing: 'info',
  planned: 'primary',
  shipped: 'success',
  wont_fix: 'default',
};

const fetchVoiceFailures = async ({ page, limit, failureType, businessType, reviewStatus }) => {
  const params = new URLSearchParams({ page, limit });
  if (failureType) params.append('failureType', failureType);
  if (businessType) params.append('businessType', businessType);
  if (reviewStatus) params.append('reviewStatus', reviewStatus);

  return apiFetch(
    `${API_BASE}/api/voice-failures?${params}`,
    { headers: getAuthHeaders() },
    { fallbackMessage: 'Failed to load Novo Voice failed commands', suppressGlobalError: true }
  );
};

const fetchVoiceFailureSummary = async () => {
  return apiFetch(
    `${API_BASE}/api/voice-failures/summary`,
    { headers: getAuthHeaders() },
    { fallbackMessage: 'Failed to load failed-command summary', suppressGlobalError: true }
  );
};

const updateVoiceFailure = async ({ id, reviewStatus, reviewNotes }) => {
  return apiFetch(
    `${API_BASE}/api/voice-failures/${id}`,
    {
      method: 'PATCH',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reviewStatus, reviewNotes }),
    },
    { fallbackMessage: 'Failed to update failed command', suppressGlobalError: true }
  );
};

const buildFilterParams = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return params;
};

const parseFilename = (contentDisposition) => {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition || '');
  return match ? match[1] : null;
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Downloads the export file and returns the window it covers, so a follow-up
// bulk update can be scoped to exactly these records.
const downloadVoiceFailureExport = async ({ format, filters }) => {
  const params = buildFilterParams(filters);
  params.append('format', format);

  const response = await fetch(`${API_BASE}/api/voice-failures/export?${params}`, {
    credentials: 'include',
    headers: {
      ...getAuthHeaders(),
      Accept: format === 'markdown' ? 'text/markdown' : 'text/csv',
    },
  });

  if (!response.ok) {
    let message = `Failed to export failed commands (status ${response.status})`;
    if ((response.headers.get('content-type') || '').includes('application/json')) {
      const data = await response.json().catch(() => ({}));
      message = data.message || data.error || message;
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const fallbackName = `novo-voice-failed-commands-${new Date().toISOString().slice(0, 10)}.${format === 'markdown' ? 'md' : 'csv'}`;
  saveBlob(blob, parseFilename(response.headers.get('content-disposition')) || fallbackName);

  return {
    format,
    filters,
    count: parseInt(response.headers.get('X-Export-Count') || '0', 10),
    total: parseInt(response.headers.get('X-Export-Total') || '0', 10),
    truncated: response.headers.get('X-Export-Truncated') === 'true',
    marked: parseInt(response.headers.get('X-Export-Marked') || '0', 10),
    from: response.headers.get('X-Export-From'),
    to: response.headers.get('X-Export-To'),
  };
};

const previewBulkReview = async ({ filters, from, to }) => {
  const params = buildFilterParams(filters);
  if (from) params.append('from', from);
  if (to) params.append('to', to);

  return apiFetch(
    `${API_BASE}/api/voice-failures/bulk-review/preview?${params}`,
    { headers: getAuthHeaders() },
    { fallbackMessage: 'Failed to count exported commands', suppressGlobalError: true }
  );
};

const applyBulkReview = async ({ filters, from, to, reviewStatus }) => {
  return apiFetch(
    `${API_BASE}/api/voice-failures/bulk-review`,
    {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ filters, from, to, reviewStatus }),
    },
    { fallbackMessage: 'Failed to update exported commands', suppressGlobalError: true }
  );
};

const VoiceCommandFailures = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [failureTypeFilter, setFailureTypeFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const [reviewStatusFilter, setReviewStatusFilter] = useState('new');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFailure, setSelectedFailure] = useState(null);
  const [editStatus, setEditStatus] = useState('new');
  const [editNotes, setEditNotes] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [exportingFormat, setExportingFormat] = useState('');
  const [lastExport, setLastExport] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery(
    ['voiceFailures', page + 1, rowsPerPage, failureTypeFilter, businessTypeFilter, reviewStatusFilter],
    () =>
      fetchVoiceFailures({
        page: page + 1,
        limit: rowsPerPage,
        failureType: failureTypeFilter,
        businessType: businessTypeFilter,
        reviewStatus: reviewStatusFilter,
      }),
    {
      keepPreviousData: true,
      onError: (queryError) => {
        setFeedback({ type: 'error', message: queryError?.message || 'Failed to load failed commands' });
      },
    }
  );

  const { data: summaryData } = useQuery('voiceFailuresSummary', fetchVoiceFailureSummary);

  const updateMutation = useMutation(updateVoiceFailure, {
    onSuccess: () => {
      queryClient.invalidateQueries('voiceFailures');
      queryClient.invalidateQueries('voiceFailuresSummary');
      setEditDialogOpen(false);
      setFeedback({ type: 'success', message: 'Failed command updated successfully.' });
    },
    onError: (mutationError) => {
      setFeedback({ type: 'error', message: mutationError?.message || 'Failed to update failed command' });
    },
  });

  const activeFilters = {
    failureType: failureTypeFilter,
    businessType: businessTypeFilter,
    reviewStatus: reviewStatusFilter,
  };

  const {
    data: resetPreview,
    isLoading: resetPreviewLoading,
    error: resetPreviewError,
  } = useQuery(
    ['voiceFailuresBulkPreview', lastExport?.from, lastExport?.to, lastExport?.filters],
    () => previewBulkReview({ filters: lastExport.filters, from: lastExport.from, to: lastExport.to }),
    { enabled: resetDialogOpen && Boolean(lastExport) }
  );

  const bulkReviewMutation = useMutation(applyBulkReview, {
    onSuccess: (result) => {
      queryClient.invalidateQueries('voiceFailures');
      queryClient.invalidateQueries('voiceFailuresSummary');
      setResetDialogOpen(false);
      setLastExport(null);
      setFeedback({
        type: 'success',
        message:
          `Moved ${result.modified} command${result.modified === 1 ? '' : 's'} to ` +
          `${REVIEW_STATUS_LABELS[RESET_TARGET_STATUS]}. Nothing was deleted — filter by ` +
          `${REVIEW_STATUS_LABELS[RESET_TARGET_STATUS]} to review them or move them back.`,
      });
    },
    onError: (mutationError) => {
      setFeedback({ type: 'error', message: mutationError?.message || 'Failed to update exported commands' });
    },
  });

  const handleExport = async (format) => {
    setExportingFormat(format);
    setFeedback({ type: '', message: '' });
    try {
      const result = await downloadVoiceFailureExport({ format, filters: activeFilters });
      setLastExport(result);
      queryClient.invalidateQueries('voiceFailures');
      queryClient.invalidateQueries('voiceFailuresSummary');
      const moved = result.marked > 0
        ? ` Moved ${result.marked} to ${REVIEW_STATUS_LABELS[RESET_TARGET_STATUS]}.`
        : '';
      setFeedback({
        type: 'success',
        message: result.truncated
          ? `Exported the ${result.count} most recent of ${result.total} matching commands. Narrow the filters to reach older ones.${moved}`
          : `Exported ${result.count} failed command${result.count === 1 ? '' : 's'}.${moved}`,
      });
    } catch (exportError) {
      setFeedback({ type: 'error', message: exportError?.message || 'Failed to export failed commands' });
    } finally {
      setExportingFormat('');
    }
  };

  const openEditDialog = (failure) => {
    setSelectedFailure(failure);
    setEditStatus(failure.reviewStatus || 'new');
    setEditNotes(failure.reviewNotes || '');
    setEditDialogOpen(true);
  };

  const businessTypeOptions = (summaryData?.summary?.byBusinessType || [])
    .map((entry) => entry._id)
    .filter(Boolean);

  const totalNewAndReviewing = (summaryData?.summary?.byReviewStatus || [])
    .filter((entry) => entry._id === 'new' || entry._id === 'reviewing')
    .reduce((sum, entry) => sum + entry.count, 0);

  const shippedCount = (summaryData?.summary?.byReviewStatus || []).find((entry) => entry._id === 'shipped')?.count || 0;

  if (isLoading) return <LoadingSpinner />;
  if (error) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error?.message || 'Error loading Novo Voice failed commands'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <VoiceIcon fontSize="small" />
        Commands users asked Novo Voice for across every business, but it couldn't understand or complete. Only
        the failed command text is collected — never other business or customer data.
      </Typography>

      {feedback.message && (
        <Alert severity={feedback.type || 'info'} sx={{ mb: 2 }} onClose={() => setFeedback({ type: '', message: '' })}>
          {feedback.message}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'flex-end', alignItems: { xs: 'stretch', sm: 'center' }, mb: 2 }}
      >
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => {
            setFeedback({ type: '', message: '' });
            refetch();
            queryClient.invalidateQueries('voiceFailuresSummary');
          }}
        >
          Refresh
        </Button>
        <Tooltip title="Download every command matching the current filters as a spreadsheet">
          <span>
            <Button
              variant="outlined"
              fullWidth
              startIcon={exportingFormat === 'csv' ? <CircularProgress size={18} /> : <DownloadIcon />}
              disabled={Boolean(exportingFormat)}
              onClick={() => handleExport('csv')}
            >
              Export CSV
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Download the same commands grouped by request and ranked by how often they were asked, formatted for a coding agent to work from">
          <span>
            <Button
              variant="outlined"
              fullWidth
              startIcon={exportingFormat === 'markdown' ? <CircularProgress size={18} /> : <ArticleIcon />}
              disabled={Boolean(exportingFormat)}
              onClick={() => handleExport('markdown')}
            >
              Export for Agents
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            lastExport
              ? `Move the ${lastExport.count} exported command${lastExport.count === 1 ? '' : 's'} to ${REVIEW_STATUS_LABELS[RESET_TARGET_STATUS]} so they leave the review queue`
              : 'Export first — this clears out exactly the commands you downloaded'
          }
        >
          <span>
            <Button
              variant="contained"
              fullWidth
              startIcon={<ClearQueueIcon />}
              disabled={!lastExport || Boolean(exportingFormat)}
              onClick={() => setResetDialogOpen(true)}
            >
              Clear Exported
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Summary Cards — Needs Review is the open queue; All Time never resets */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {summaryData?.summary?.unresolved ?? totalNewAndReviewing}
              </Typography>
              <Typography variant="caption">Needs Review</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="h4">{summaryData?.summary?.total ?? 0}</Typography>
              <Typography variant="caption">All Time</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">{shippedCount}</Typography>
              <Typography variant="caption">Shipped</Typography>
            </CardContent>
          </Card>
        </Grid>
        {(summaryData?.summary?.byFailureType || []).slice(0, 3).map((entry) => (
          <Grid item xs={6} sm={4} md={2} key={entry._id}>
            <Card>
              <CardContent>
                <Typography variant="h4">{entry.count}</Typography>
                <Typography variant="caption">{FAILURE_TYPE_LABELS[entry._id] || entry._id}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Top Failing Phrases */}
      {summaryData?.topPhrases?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Most Common Unresolved Phrases
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Phrase</TableCell>
                    <TableCell align="right">Times Asked</TableCell>
                    <TableCell align="right">Businesses</TableCell>
                    <TableCell>Failure Types</TableCell>
                    <TableCell>Last Seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryData.topPhrases.map((phrase) => (
                    <TableRow key={phrase.sampleId}>
                      <TableCell>{phrase.utteranceNormalized}</TableCell>
                      <TableCell align="right">{phrase.count}</TableCell>
                      <TableCell align="right">{phrase.companyCount}</TableCell>
                      <TableCell>
                        {(phrase.failureTypes || []).map((type) => (
                          <Chip
                            key={type}
                            size="small"
                            label={FAILURE_TYPE_LABELS[type] || type}
                            color={FAILURE_TYPE_COLORS[type] || 'default'}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {phrase.lastOccurredAt
                          ? formatDistanceToNow(new Date(phrase.lastOccurredAt), { addSuffix: true })
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>Failure Type</InputLabel>
            <Select
              value={failureTypeFilter}
              label="Failure Type"
              onChange={(e) => {
                setFailureTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(FAILURE_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>Review Status</InputLabel>
            <Select
              value={reviewStatusFilter}
              label="Review Status"
              onChange={(e) => {
                setReviewStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(REVIEW_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {businessTypeOptions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>Business Type</InputLabel>
              <Select
                value={businessTypeFilter}
                label="Business Type"
                onChange={(e) => {
                  setBusinessTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {businessTypeOptions.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </CardContent>
      </Card>

      {/* Failures Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Command</TableCell>
              <TableCell>Failure Type</TableCell>
              <TableCell>Business</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Occurred</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items?.map((failure) => (
              <TableRow key={failure._id}>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{failure.utterance}"
                  </Typography>
                  {failure.assistantMessage && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Novo said: {failure.assistantMessage}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={FAILURE_TYPE_LABELS[failure.failureType] || failure.failureType}
                    color={FAILURE_TYPE_COLORS[failure.failureType] || 'default'}
                  />
                </TableCell>
                <TableCell>{failure.businessType || '—'}</TableCell>
                <TableCell>{failure.companyName || failure.companyId}</TableCell>
                <TableCell>{failure.channel}</TableCell>
                <TableCell>
                  {failure.occurredAt
                    ? formatDistanceToNow(new Date(failure.occurredAt), { addSuffix: true })
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={REVIEW_STATUS_LABELS[failure.reviewStatus] || failure.reviewStatus}
                    color={REVIEW_STATUS_COLORS[failure.reviewStatus] || 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Update status / notes">
                    <IconButton size="small" color="primary" onClick={() => openEditDialog(failure)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {data?.items?.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No failed commands match these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.pagination?.total || 0}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Failed Command</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1, fontStyle: 'italic' }}>
            "{selectedFailure?.utterance}"
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Review Status</InputLabel>
                <Select value={editStatus} label="Review Status" onChange={(e) => setEditStatus(e.target.value)}>
                  {Object.entries(REVIEW_STATUS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="e.g. Adding support for this in v2.4"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              updateMutation.mutate({ id: selectedFailure?._id, reviewStatus: editStatus, reviewNotes: editNotes })
            }
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear-exported confirmation */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clear exported commands from the queue?</DialogTitle>
        <DialogContent>
          {resetPreviewLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Counting the commands this will change…</Typography>
            </Box>
          )}

          {resetPreviewError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {resetPreviewError?.message || 'Could not count the exported commands.'}
            </Alert>
          )}

          {!resetPreviewLoading && !resetPreviewError && (
            <>
              <Typography variant="body1" sx={{ mt: 1 }}>
                This will move <strong>{resetPreview?.count ?? 0}</strong> command
                {(resetPreview?.count ?? 0) === 1 ? '' : 's'} to{' '}
                <strong>{REVIEW_STATUS_LABELS[RESET_TARGET_STATUS]}</strong>, which takes them out of
                Needs Review and out of the Most Common Unresolved Phrases list.
              </Typography>

              <Alert severity="info" sx={{ mt: 2 }}>
                Nothing is deleted. The commands, how often each was asked, and which businesses asked
                stay in the database — filter by {REVIEW_STATUS_LABELS[RESET_TARGET_STATUS]} to read them
                again or move them back.
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Only the {lastExport?.count ?? 0} command
                {(lastExport?.count ?? 0) === 1 ? '' : 's'} in the file you just downloaded are affected.
                Failures recorded after that download stay in the queue.
              </Typography>

              {lastExport?.to && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Covers failures recorded up to {new Date(lastExport.to).toLocaleString()}.
                </Typography>
              )}

              {(resetPreview?.count ?? 0) !== (lastExport?.count ?? 0) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Fewer than the {lastExport?.count ?? 0} exported because commands already marked
                  Planned, Shipped, or Won't Fix are left as they are.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={bulkReviewMutation.isLoading || resetPreviewLoading || !resetPreview?.count}
            onClick={() =>
              bulkReviewMutation.mutate({
                filters: lastExport?.filters,
                from: lastExport?.from,
                to: lastExport?.to,
                reviewStatus: RESET_TARGET_STATUS,
              })
            }
          >
            {bulkReviewMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              `Move ${resetPreview?.count ?? 0} to ${REVIEW_STATUS_LABELS[RESET_TARGET_STATUS]}`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceCommandFailures;
